import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
import { getTenantById, getTenants, deleteTenant } from "wasp/client/operations";
import { toast } from "../../hooks/use-toast";
import { useAuth } from "wasp/client/auth";
import {
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  User,
  Edit3,
  Trash2,
  Clock,
  Tag,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { SubscribeButton } from "../content/components/SubscribeButton";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger,
} from "../../components/layout";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";

export default function ViewTenantPage() {
  useRequireSectionVisible("showTenantBusiness");
  const { tenantId } = useParams<{ tenantId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const deleteTenantAction = useAction(deleteTenant);

  // Fetch tenant data
  const {
    data: tenant,
    isLoading,
    error,
  } = useQuery(getTenantById, { tenantId: tenantId! });

  // Fetch all tenants for navigation
  const { data: allTenants = [] } = useQuery(getTenants, {
    filters: {},
  });

  // Find current tenant index and navigation targets
  const currentIndex = allTenants.findIndex((t) => t.id === tenantId);
  const previousTenant = currentIndex > 0 ? allTenants[currentIndex - 1] : null;
  const nextTenant =
    currentIndex < allTenants.length - 1 ? allTenants[currentIndex + 1] : null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "text-green-600 bg-green-50 border-green-200";
      case "INACTIVE":
        return "text-gray-600 bg-gray-50 border-gray-200";
      case "PENDING":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "SUSPENDED":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      Retail: "text-blue-600 bg-blue-50 border-blue-200",
      "Food & Beverage": "text-orange-600 bg-orange-50 border-orange-200",
      Services: "text-purple-600 bg-purple-50 border-purple-200",
      Entertainment: "text-pink-600 bg-pink-50 border-pink-200",
      Healthcare: "text-green-600 bg-green-50 border-green-200",
      Education: "text-indigo-600 bg-indigo-50 border-indigo-200",
      Other: "text-gray-600 bg-gray-50 border-gray-200",
    };
    return colors[category] || colors["Other"];
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600">
              You must be logged in to view tenants.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading tenant...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !tenant) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Tenant Not Found
            </h3>
            <p className="text-gray-600 mb-4">
              The tenant you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => navigate("/tenants")}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Tenants
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "About",
      dropdownItems: [
        { label: "Notices", href: "/notices" },
        { label: "Events/Activities", href: "/events" },
        { label: "Tenants", href: "/tenants" },
        { label: "Facilities", href: "/facilities" },
      ],
    },
    {
      label: "Tenants",
      isClickable: true,
      href: "/tenants",
    },
  ];

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        {/* Navigation Buttons */}
        <div className="flex gap-2 mb-4 pb-4 border-b border-border">
          <button
            onClick={() =>
              previousTenant && navigate(`/tenants/${previousTenant.id}`)
            }
            disabled={!previousTenant}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border rounded-lg transition-colors ${previousTenant
              ? "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              : "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
              }`}
            title={
              previousTenant
                ? `Previous: ${previousTenant.companyName}`
                : "No previous tenant"
            }
          >
            <ChevronUp className="w-4 h-4" />
            <span className="text-sm font-medium">Previous</span>
          </button>
          <button
            onClick={() => nextTenant && navigate(`/tenants/${nextTenant.id}`)}
            disabled={!nextTenant}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border rounded-lg transition-colors ${nextTenant
              ? "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              : "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
              }`}
            title={
              nextTenant ? `Next: ${nextTenant.companyName}` : "No next tenant"
            }
          >
            <span className="text-sm font-medium">Next</span>
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        <ActionButtons>
          <ActionButtonLink to={`/tenants/${tenant.id}/edit`} icon={Edit3}>
            Edit Tenant
          </ActionButtonLink>
          <ActionButtonDanger
            onClick={async () => {
              if (confirm("Are you sure you want to delete this tenant?")) {
                try {
                  await deleteTenantAction({ tenantId: tenant.id });
                  toast({ title: "Success", description: "Tenant deleted successfully" });
                  navigate("/tenants");
                } catch (err: any) {
                  toast({ title: "Error", description: err.message || "Failed to delete tenant", variant: "destructive" });
                }
              }
            }}
            icon={Trash2}
          >
            Delete Tenant
          </ActionButtonDanger>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Category" value={tenant.category || "N/A"} />
          <DetailField label="Status" value={tenant.status || "N/A"} />
          {tenant.isVerified && (
            <DetailField label="Verification" value="Verified ✓" />
          )}
          {tenant.isFeatured && <DetailField label="Featured" value="Yes ⭐" />}
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      {/* Tenant Header */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-start gap-6">
          {tenant.logoUrl && (
            <div className="flex-shrink-0">
              <img
                src={tenant.logoUrl}
                alt={tenant.companyName}
                className="w-24 h-24 object-cover rounded-lg border border-border"
              />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center justify-between gap-4 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {tenant.companyName}
                  </h1>
                  <SubscribeButton contentId={tenant.id} />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium border rounded ${getCategoryColor(
                      tenant.category,
                    )}`}
                  >
                    <Tag className="w-4 h-4" />
                    {tenant.category}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium border rounded ${getStatusColor(
                      tenant.status,
                    )}`}
                  >
                    {tenant.status === "ACTIVE" ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    {tenant.status}
                  </span>
                  {tenant.isVerified && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium border rounded text-blue-600 bg-blue-50 border-blue-200">
                      <CheckCircle className="w-4 h-4" />
                      Verified
                    </span>
                  )}
                  {tenant.isFeatured && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium border rounded text-yellow-600 bg-yellow-50 border-yellow-200">
                      ⭐ Featured
                    </span>
                  )}
                </div>
              </div>
            </div>

            {tenant.description && (
              <p className="text-gray-700 mb-4">{tenant.description}</p>
            )}

            {/* Services */}
            {tenant.services && tenant.services.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  Services Offered:
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tenant.services.map((service, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Contact Information
          </h2>
          <div className="space-y-3">
            {tenant.contactPerson && (
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Contact Person</p>
                  <p className="text-gray-900">{tenant.contactPerson}</p>
                </div>
              </div>
            )}
            {tenant.phone && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <a
                    href={`tel:${tenant.phone}`}
                    className="text-blue-600 hover:underline"
                  >
                    {tenant.phone}
                  </a>
                </div>
              </div>
            )}
            {tenant.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <a
                    href={`mailto:${tenant.email}`}
                    className="text-blue-600 hover:underline"
                  >
                    {tenant.email}
                  </a>
                </div>
              </div>
            )}
            {tenant.website && (
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Website</p>
                  <a
                    href={tenant.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    {tenant.website}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Location Details */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Location Details
          </h2>
          <div className="space-y-3">
            {tenant.building && (
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Building</p>
                  <p className="text-gray-900">{tenant.building}</p>
                </div>
              </div>
            )}
            {tenant.floor && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Floor</p>
                  <p className="text-gray-900">{tenant.floor}</p>
                </div>
              </div>
            )}
            {tenant.unitNumber && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Unit Number</p>
                  <p className="text-gray-900">{tenant.unitNumber}</p>
                </div>
              </div>
            )}
            {tenant.businessHours && (
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Business Hours</p>
                  <p className="text-gray-900">{tenant.businessHours}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gallery */}
      {tenant.galleryImages && tenant.galleryImages.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tenant.galleryImages.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`${tenant.companyName} gallery ${index + 1}`}
                className="w-full h-48 object-cover rounded-lg border border-border"
              />
            ))}
          </div>
        </div>
      )}
    </IndividualViewPageLayout>
  );
}
